import React from "react";
import GridItem from "components/Grid/GridItem.jsx";
import GridContainer from "components/Grid/GridContainer.jsx";
import Card from "components/Card/Card.jsx";
import CardHeader from "components/Card/CardHeader.jsx";
import {
  Table, TableHead, TableBody, TableCell, TableRow,
  Button, Dialog, DialogActions, DialogContent, DialogTitle,
  TextField, Slide, MenuItem, CircularProgress, IconButton, Box, Icon,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import LocalHospitalOutlinedIcon from "@material-ui/icons/LocalHospitalOutlined";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import SwitchToggle from "components/SwitchToggle/SwitchToggle";
import SearchableSelect from "components/SearchableSelect/SearchableSelect";
import { FormErrors } from "../../components/Login/FormErrors";
import { userService } from "_services/user.service";
import { apiPath } from "api";
import { generalAuthTokenHeader } from "__helpers/auth-header";
import { EMAIL_REGEX } from "__helpers/constants";

const LEVEL_LABEL = { EXECUTIVE: "Exec", SVPO: "SVPO", VPO: "VPO", ADMIN: "Admin" };
const groupLevelLabel = { EXECUTIVE: "Executive", SVPO: "SVPO group", VPO: "VPO group", ADMIN: "Admin group" };

const Transition = React.forwardRef((props, ref) => <Slide direction="down" ref={ref} {...props} />);

export default class Managers extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      managers: [],
      groups: [],
      facilities: [],
      search: "",
      modalStatus: false,
      saving: false,
      form: { firstName: "", lastName: "", email: "", groupId: "" },
      formErrors: { firstName: "", lastName: "", email: "", groupId: "" },
      directFacilityIds: [],
      pickFacilityId: "",
      // view dialog
      viewOpen: false,
      viewManager: null,
      viewDirectIds: [],
      viewLoading: false,
      // edit dialog
      editOpen: false,
      editManager: null,
      eForm: { firstName: "", lastName: "", groupId: "" },
      eDirectIds: [],
      ePickFacilityId: "",
      editSaving: false,
    };
  }

  componentDidMount() {
    this.fetchManagers();
    this.fetchGroups();
    this.fetchFacilities();
  }

  notify = (message, type) => userService.showNotification({ title: "Managers", message, type });

  // Pull a clean message out of an RFC-7807 error response (detail holds the reason).
  // Strips Spring's `409 CONFLICT "…"` wrapper so only the human reason is shown.
  extractError = async (response, fallback) => {
    try {
      const t = await response.text();
      if (!t) return fallback;
      let msg;
      try { const j = JSON.parse(t); msg = j.detail || j.title || fallback; } catch (e) { msg = t; }
      if (typeof msg === "string") {
        const m = msg.match(/^\d{3}\s.*?"([\s\S]*)"\s*$/);
        if (m) msg = m[1];
      }
      return msg || fallback;
    } catch (e) { return fallback; }
  };

  fetchManagers = async (search) => {
    try {
      const url = `${apiPath.groups}/managers/all` + (search ? `?search=${encodeURIComponent(search)}` : "");
      const r = await fetch(url, { method: "GET", headers: generalAuthTokenHeader() });
      const data = r.ok ? await r.json() : [];
      this.setState({ managers: Array.isArray(data) ? data : [] });
    } catch (e) { /* ignore */ }
  };

  fetchGroups = async () => {
    try {
      const r = await fetch(apiPath.groups, { method: "GET", headers: generalAuthTokenHeader() });
      const data = r.ok ? await r.json() : [];
      this.setState({ groups: Array.isArray(data) ? data : [] });
    } catch (e) { /* ignore */ }
  };

  fetchFacilities = async () => {
    try {
      const r = await fetch(`${apiPath.facilitiesList}?page=0&size=1000`, { method: "GET", headers: generalAuthTokenHeader() });
      const data = r.ok ? await r.json() : [];
      this.setState({ facilities: Array.isArray(data) ? data : [] });
    } catch (e) { /* ignore */ }
  };

  onSearch = (e) => {
    const v = e.target.value;
    this.setState({ search: v });
    this.fetchManagers(v);
  };

  toggleStatus = async (m) => {
    try {
      const activate = m.status !== "ACTIVE";
      const r = await fetch(`${apiPath.groups}/${m.orgId}/managers/${m.userId}/status?activated=${activate}`, {
        method: "PUT", headers: generalAuthTokenHeader(),
      });
      if (r.ok) this.fetchManagers(this.state.search);
      else this.notify("Could not update status", "danger");
    } catch (e) { this.notify("Server error", "danger"); }
  };

  modalOpen = () => this.setState({
    modalStatus: true,
    form: { firstName: "", lastName: "", email: "", groupId: "" },
    formErrors: { firstName: "", lastName: "", email: "", groupId: "" },
    directFacilityIds: [],
    pickFacilityId: "",
  });
  modalClose = () => this.setState({ modalStatus: false });

  handleField = (e) => {
    const { name, value } = e.target;
    this.setState((s) => ({ form: { ...s.form, [name]: value } }));
  };

  facilityName = (id) => {
    const f = this.state.facilities.find((x) => String(x.facilityId) === String(id));
    return f ? f.facilityName : `#${id}`;
  };

  addDirect = () => {
    const { pickFacilityId, directFacilityIds } = this.state;
    if (!pickFacilityId) return;
    if (directFacilityIds.some((id) => String(id) === String(pickFacilityId))) return;
    this.setState({ directFacilityIds: [...directFacilityIds, Number(pickFacilityId)], pickFacilityId: "" });
  };
  removeDirect = (id) => this.setState((s) => ({ directFacilityIds: s.directFacilityIds.filter((x) => x !== id) }));

  validate = () => {
    const { form } = this.state;
    const errs = { firstName: "", lastName: "", email: "", groupId: "" };
    let ok = true;
    if (!form.firstName.trim()) { errs.firstName = "First name is required"; ok = false; }
    if (!form.lastName.trim()) { errs.lastName = "Last name is required"; ok = false; }
    if (!form.email.trim() || !EMAIL_REGEX.test(form.email.trim())) { errs.email = "Valid email is required"; ok = false; }
    if (!form.groupId) { errs.groupId = "Select a group"; ok = false; }
    this.setState({ formErrors: errs });
    return ok;
  };

  save = async () => {
    if (!this.validate()) return;
    const { form, directFacilityIds } = this.state;
    this.setState({ saving: true });
    try {
      const body = {
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        email: form.email.trim(),
        directFacilityIds: directFacilityIds,
      };
      const r = await fetch(`${apiPath.groups}/${form.groupId}/managers`, {
        method: "POST", headers: generalAuthTokenHeader(), body: JSON.stringify(body),
      });
      if (r.ok) {
        this.notify("Manager assigned", "success");
        this.setState({ modalStatus: false, saving: false });
        this.fetchManagers(this.state.search);
      } else {
        const t = await r.text();
        this.notify(t || "Could not assign manager", "danger");
        this.setState({ saving: false });
      }
    } catch (e) {
      this.notify("Server error", "danger");
      this.setState({ saving: false });
    }
  };

  // ---------------------------------------------------------------- view / edit
  fetchDirectIds = async (userId) => {
    try {
      const r = await fetch(`${apiPath.groups}/managers/${userId}/facilities`, { method: "GET", headers: generalAuthTokenHeader() });
      const data = r.ok ? await r.json() : [];
      return Array.isArray(data) ? data : [];
    } catch (e) { return []; }
  };

  openView = async (m) => {
    this.setState({ viewOpen: true, viewManager: m, viewDirectIds: [], viewLoading: true });
    const ids = await this.fetchDirectIds(m.userId);
    this.setState({ viewDirectIds: ids, viewLoading: false });
  };
  closeView = () => this.setState({ viewOpen: false });

  openEdit = async (m) => {
    this.setState({
      editOpen: true, editManager: m,
      eForm: { firstName: m.firstName || "", lastName: m.lastName || "", groupId: m.orgId || "" },
      eActive: m.status === "ACTIVE",
      eDirectIds: [], ePickFacilityId: "", editSaving: false,
    });
    const ids = await this.fetchDirectIds(m.userId);
    this.setState({ eDirectIds: ids });
  };
  closeEdit = () => this.setState({ editOpen: false });

  eField = (e) => {
    const { name, value } = e.target;
    this.setState((s) => ({ eForm: { ...s.eForm, [name]: value } }));
  };

  // Status only changes locally; it is persisted when the user clicks Update.
  toggleEditActive = () => this.setState((s) => ({ eActive: !s.eActive }));

  // Single Update: persists name + group reassignment + status together.
  saveManager = async () => {
    const { editManager, eForm, eActive } = this.state;
    if (!eForm.firstName.trim() || !eForm.lastName.trim()) { this.notify("First and last name are required", "danger"); return; }
    if (!eForm.groupId) { this.notify("Select a group", "danger"); return; }
    this.setState({ editSaving: true });
    try {
      const body = { firstName: eForm.firstName.trim(), lastName: eForm.lastName.trim(), orgId: eForm.groupId };
      const r = await fetch(`${apiPath.groups}/managers/${editManager.userId}`, {
        method: "PUT", headers: generalAuthTokenHeader(), body: JSON.stringify(body),
      });
      if (!r.ok) { this.notify(await this.extractError(r, "Could not update manager"), "danger"); this.setState({ editSaving: false }); return; }
      // Persist status against the (possibly reassigned) group.
      const rs = await fetch(`${apiPath.groups}/${eForm.groupId}/managers/${editManager.userId}/status?activated=${eActive}`, {
        method: "PUT", headers: generalAuthTokenHeader(),
      });
      if (!rs.ok) { this.notify("Could not update status", "danger"); this.setState({ editSaving: false }); return; }
      this.notify("Manager updated", "success");
      this.setState({ editOpen: false, editSaving: false });
      this.fetchManagers(this.state.search);
    } catch (e) { this.notify("Server error", "danger"); this.setState({ editSaving: false }); }
  };

  eAddDirect = async () => {
    const { editManager, ePickFacilityId, eDirectIds } = this.state;
    if (!ePickFacilityId) return;
    if (eDirectIds.some((id) => String(id) === String(ePickFacilityId))) { this.setState({ ePickFacilityId: "" }); return; }
    try {
      const r = await fetch(`${apiPath.groups}/managers/${editManager.userId}/facilities/${ePickFacilityId}`, { method: "POST", headers: generalAuthTokenHeader() });
      if (r.ok) { this.setState((s) => ({ eDirectIds: [...s.eDirectIds, Number(ePickFacilityId)], ePickFacilityId: "" })); this.fetchManagers(this.state.search); }
      else this.notify("Could not add facility", "danger");
    } catch (e) { this.notify("Server error", "danger"); }
  };
  eRemoveDirect = async (facilityId) => {
    const { editManager } = this.state;
    try {
      const r = await fetch(`${apiPath.groups}/managers/${editManager.userId}/facilities/${facilityId}`, { method: "DELETE", headers: generalAuthTokenHeader() });
      if (r.ok) { this.setState((s) => ({ eDirectIds: s.eDirectIds.filter((x) => String(x) !== String(facilityId)) })); this.fetchManagers(this.state.search); }
      else this.notify("Could not remove facility", "danger");
    } catch (e) { this.notify("Server error", "danger"); }
  };

  render() {
    const { managers, groups, facilities, form, formErrors, directFacilityIds } = this.state;
    return (
      <GridContainer>
        <Card className={"advert-cover"}>
          <GridItem xs={12} sm={12} md={12}>
            <CardHeader color="success" className={"cusCardheader cusCardTitle"}>
              <h4>Managers</h4>
            </CardHeader>
          </GridItem>

          <div className={"custom-container"}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap" }}>
              <div className="search-btn-cover" style={{ float: "none" }}>
                <input type="text" placeholder="Search by name, email or group." className="search-btn" onChange={this.onSearch} />
                <i className="material-icons">search</i>
              </div>
              <div className="button-cover" style={{ float: "none" }}>
                <button className="pointer btn1" onClick={this.modalOpen}>Invite / Assign Manager</button>
              </div>
            </div>

            <div className="tableResponsive" style={{ clear: "both" }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Manages (group)</TableCell>
                    <TableCell>Scope</TableCell>
                    <TableCell align="center">Status</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {managers.length ? (
                    managers.map((m) => (
                      <TableRow key={`${m.orgId}-${m.userId}`}>
                        <TableCell>
                          <strong>{(m.firstName || "") + " " + (m.lastName || "")}</strong>
                          <div style={{ color: "#6b6969", fontSize: "0.82rem" }}>{m.email}</div>
                        </TableCell>
                        <TableCell>
                          {m.orgName ? (
                            <span>{m.orgName} <em style={{ color: "#00706f" }}>({LEVEL_LABEL[m.groupType] || m.groupType})</em></span>
                          ) : "—"}
                        </TableCell>
                        <TableCell>{m.facilityScope != null ? `${m.facilityScope} facilities` : "—"}</TableCell>
                        <TableCell align="center">
                          <div style={{ display: "flex", justifyContent: "center" }}>
                            <SwitchToggle id={`${m.orgId}-${m.userId}`} status={m.status === "ACTIVE"} clickHandler={() => this.toggleStatus(m)} />
                          </div>
                        </TableCell>
                        <TableCell style={{ whiteSpace: "nowrap" }}>
                          {/* View action hidden for now
                          <span className="edit-action" title="View manager" onClick={() => this.openView(m)} style={{ marginRight: 12 }}>
                            <Icon className="fa fa-eye" aria-hidden="true" />
                          </span>
                          */}
                          <span className="edit-action" title="Edit manager" onClick={() => this.openEdit(m)}>
                            <Icon className="fa fa-pencil" aria-hidden="true" />
                          </span>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan="5"><div className="not-found-text">No managers found.</div></TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </div>
        </Card>

        {/* Invite / Assign Manager dialog */}
        <Dialog open={this.state.modalStatus} maxWidth={"sm"} fullWidth onClose={this.modalClose} TransitionComponent={Transition} className="add-modal">
          <DialogTitle className="add-modal-title">
            Invite / Assign Manager
            <IconButton className="closeButton" aria-label="close" onClick={this.modalClose}><CloseIcon /></IconButton>
          </DialogTitle>
          <DialogContent>
            <Box display="flex" mb={2} mt={1}>
              <Box width={1 / 2} pr={1}>
                <TextField fullWidth name="firstName" value={form.firstName} onChange={this.handleField} label="First Name *" />
                <FormErrors show={true} formErrors={formErrors} fieldName="firstName" />
              </Box>
              <Box width={1 / 2} pl={1}>
                <TextField fullWidth name="lastName" value={form.lastName} onChange={this.handleField} label="Last Name *" />
                <FormErrors show={true} formErrors={formErrors} fieldName="lastName" />
              </Box>
            </Box>
            <Box mb={2}>
              <TextField fullWidth name="email" value={form.email} onChange={this.handleField} label="Email *" />
              <FormErrors show={true} formErrors={formErrors} fieldName="email" />
            </Box>
            <Box mb={2}>
              <SearchableSelect
                label="Manages this group *"
                placeholder="-- select group --"
                value={form.groupId}
                onChange={(v) => this.setState((s) => ({ form: { ...s.form, groupId: v } }))}
                options={groups
                  .filter((g) => g.active !== false && !(g.managerNames || []).some((n) => n && n.trim()))
                  .map((g) => ({ value: g.id, label: `${g.groupName} — ${groupLevelLabel[g.groupType] || g.groupType}` }))}
              />
              <FormErrors show={true} formErrors={formErrors} fieldName="groupId" />
            </Box>

            <div style={{ fontWeight: 700, fontSize: "0.8rem", margin: "8px 0 4px", textTransform: "uppercase", color: "#6b6969" }}>
              Direct facility assignments (optional)
            </div>
            <div style={{ fontSize: "0.8rem", color: "#6b6969", marginBottom: 6 }}>
              Beyond their group, pin individual facilities directly to this manager.
            </div>
            {directFacilityIds.map((id) => (
              <div key={id} className="dlg-list-row">
                <LocalHospitalOutlinedIcon className="dlg-list-icon" />
                <span className="dlg-list-text">{this.facilityName(id)}</span>
                <DeleteOutlineIcon className="dlg-remove-icon" titleAccess="Remove" onClick={() => this.removeDirect(id)} />
              </div>
            ))}
            <Box display="flex" alignItems="center" mt={1}>
              <Box flex={1} pr={1}>
                <SearchableSelect
                  label="Facility"
                  placeholder="-- select facility --"
                  value={this.state.pickFacilityId}
                  onChange={(v) => this.setState({ pickFacilityId: v })}
                  options={facilities
                    .filter((f) => !directFacilityIds.some((id) => String(id) === String(f.facilityId)))
                    .map((f) => ({ value: f.facilityId, label: f.facilityName }))}
                />
              </Box>
              <Button className="dlg-add-btn" onClick={this.addDirect}>Add facility</Button>
            </Box>
          </DialogContent>
          <DialogActions className="modal-actions">
            <Button type="button" className="btn1" onClick={this.save} disabled={this.state.saving}>
              {this.state.saving && <CircularProgress size={24} className="buttonProgress" />}
              Save
            </Button>
            <Button onClick={this.modalClose} className="btn2" color="primary">Cancel</Button>
          </DialogActions>
        </Dialog>

        {/* View manager */}
        <Dialog open={this.state.viewOpen} maxWidth={"sm"} fullWidth onClose={this.closeView} TransitionComponent={Transition} className="add-modal">
          <DialogTitle className="add-modal-title">
            Manager Details
            <IconButton className="closeButton" aria-label="close" onClick={this.closeView}><CloseIcon /></IconButton>
          </DialogTitle>
          <DialogContent>
            {this.state.viewManager && (
              <div style={{ fontSize: "0.88rem" }}>
                <div style={{ display: "flex", justifyContent: "space-between", padding: "4px 0" }}>
                  <strong>{((this.state.viewManager.firstName || "") + " " + (this.state.viewManager.lastName || "")).trim()}</strong>
                  <span>{this.state.viewManager.status === "ACTIVE" ? "Active" : "Inactive"}</span>
                </div>
                <div style={{ color: "#6b6969", padding: "2px 0" }}>Email: {this.state.viewManager.email}</div>
                <div style={{ color: "#6b6969", padding: "2px 0" }}>
                  Manages: {this.state.viewManager.orgName ? `${this.state.viewManager.orgName} (${LEVEL_LABEL[this.state.viewManager.groupType] || this.state.viewManager.groupType})` : "—"}
                </div>
                <div style={{ color: "#6b6969", padding: "2px 0" }}>Facility scope: {this.state.viewManager.facilityScope != null ? `${this.state.viewManager.facilityScope} facilities` : "—"}</div>

                <div style={{ fontWeight: 700, fontSize: "0.78rem", textTransform: "uppercase", color: "#6b6969", margin: "12px 0 4px" }}>Direct facilities</div>
                {this.state.viewLoading ? <div style={{ color: "#9a9a9a" }}>…</div> : (
                  this.state.viewDirectIds.length ? this.state.viewDirectIds.map((id) => (
                    <div key={id} className="dlg-list-row">
                      <LocalHospitalOutlinedIcon className="dlg-list-icon" />
                      <span className="dlg-list-text">{this.facilityName(id)}</span>
                    </div>
                  )) : <div style={{ color: "#9a9a9a" }}>None</div>
                )}
              </div>
            )}
          </DialogContent>
          <DialogActions className="modal-actions">
            <Button onClick={this.closeView} className="btn2" color="primary">Close</Button>
          </DialogActions>
        </Dialog>

        {/* Edit manager */}
        <Dialog open={this.state.editOpen} maxWidth={"sm"} fullWidth onClose={this.closeEdit} TransitionComponent={Transition} className="add-modal">
          <DialogTitle className="add-modal-title">
            Edit Manager
            <IconButton className="closeButton" aria-label="close" onClick={this.closeEdit}><CloseIcon /></IconButton>
          </DialogTitle>
          <DialogContent>
            <Box display="flex" mb={2} mt={1}>
              <Box width={1 / 2} pr={1}>
                <TextField fullWidth name="firstName" value={this.state.eForm.firstName} onChange={this.eField} label="First Name *" />
              </Box>
              <Box width={1 / 2} pl={1}>
                <TextField fullWidth name="lastName" value={this.state.eForm.lastName} onChange={this.eField} label="Last Name *" />
              </Box>
            </Box>
            <Box mb={2}>
              <TextField fullWidth disabled label="Email (locked)" value={this.state.editManager ? this.state.editManager.email : ""} />
            </Box>
            <Box mb={2}>
              <SearchableSelect
                label="Manages this group *"
                placeholder="-- select group --"
                value={this.state.eForm.groupId}
                onChange={(v) => this.setState((s) => ({ eForm: { ...s.eForm, groupId: v } }))}
                options={groups.map((g) => ({ value: g.id, label: `${g.groupName} — ${groupLevelLabel[g.groupType] || g.groupType}` }))}
              />
            </Box>
            <Box display="flex" alignItems="center" mb={2}>
              <span style={{ marginRight: 10, color: "#6b6969" }}>Status:</span>
              {this.state.editManager && (
                <SwitchToggle id={`edit-mgr-${this.state.editManager.userId}`} status={this.state.eActive} clickHandler={this.toggleEditActive} />
              )}
            </Box>

            <div style={{ fontWeight: 700, fontSize: "0.78rem", textTransform: "uppercase", color: "#6b6969", margin: "8px 0 4px" }}>Direct facilities</div>
            {this.state.eDirectIds.map((id) => (
              <div key={id} className="dlg-list-row">
                <LocalHospitalOutlinedIcon className="dlg-list-icon" />
                <span className="dlg-list-text">{this.facilityName(id)}</span>
                <DeleteOutlineIcon className="dlg-remove-icon" titleAccess="Remove" onClick={() => this.eRemoveDirect(id)} />
              </div>
            ))}
            <Box display="flex" alignItems="center" mt={1}>
              <Box flex={1} pr={1}>
                <SearchableSelect
                  label="Facility"
                  placeholder="-- select facility --"
                  value={this.state.ePickFacilityId}
                  onChange={(v) => this.setState({ ePickFacilityId: v })}
                  options={facilities
                    .filter((f) => !this.state.eDirectIds.some((id) => String(id) === String(f.facilityId)))
                    .map((f) => ({ value: f.facilityId, label: f.facilityName }))}
                />
              </Box>
              <Button className="dlg-add-btn" onClick={this.eAddDirect}>Add facility</Button>
            </Box>
          </DialogContent>
          <DialogActions className="modal-actions">
            <Button type="button" className="btn1" onClick={this.saveManager} disabled={this.state.editSaving}>
              {this.state.editSaving && <CircularProgress size={20} className="buttonProgress" />}
              Update
            </Button>
            <Button onClick={this.closeEdit} className="btn2" color="primary">Cancel</Button>
          </DialogActions>
        </Dialog>
      </GridContainer>
    );
  }
}
